import * as React from 'react';

// API
import apibridge from '../../apibridge';

// REDUX
import { useAppSelector } from '../../store';

// UTILS
import { validationErrorsToString } from '../../utils';

// PACKAGES
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Accordion from '../../components/Accordion';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import LoadingSpinner from '../../components/LoadingSpinner';
import NoInfoMessage from '../../components/NoInfoMessage';

// TYPES
import { ChildSafeStandardUserDashboardChildSafeStandard } from '../../api/models';

const ChildSafeStandards: React.FC = () => {
	// Getting color list from redux store
	const childSafeStandardColors = useAppSelector((state) => state.lists.childSafeStandardColors);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// State to hold fetch data
	const [childSafeStandardsData, setChildSafeStandardsData] = React.useState<
		ChildSafeStandardUserDashboardChildSafeStandard[]
	>([]);

	const getChildSafeStandardsData = async () => {
		setIsLoading(true);

		const response = await apibridge.getChildSafeStandardsData();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else if (response.result) {
			setIsLoading(false);

			setChildSafeStandardsData(response.result);
		} else {
			setIsLoading(false);
		}
	};

	React.useEffect(() => {
		getChildSafeStandardsData();
	}, []);

	// Reorder Data
	const reorderData = (data: ChildSafeStandardUserDashboardChildSafeStandard[]) => {
		const orderedData = data.sort((a, b) => {
			const orderA = a.order || 1;
			const orderB = b.order || -1;
			return orderA < orderB ? -1 : orderA > orderB ? 1 : 0;
		});

		setChildSafeStandardsData(orderedData);
	};

	React.useEffect(() => {
		reorderData(childSafeStandardsData);
	}, [childSafeStandardsData]);

	return (
		<div className="page-general-layout">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<div className="accordion-general-wrapper">
									<p className="sub-grey-text mbtom-48">
										The Child Safe Standards are a set of standards that all child-related organisations must meet if
										they are to be considered child-safe. Click on the coloured buttons below to find out more about
										each standard and actions your organisation can take to be child-safe.
									</p>

									<ul className="accordion">
										{childSafeStandardsData && childSafeStandardsData.length > 0 ? (
											childSafeStandardsData.map((item, index) => {
												return (
													<Accordion
														key={item.id}
														heading={item.title}
														content={item.description}
														colorCode={
															(childSafeStandardColors && index < 11 && childSafeStandardColors[index].value) ||
															(childSafeStandardColors && childSafeStandardColors[10].value)
														}
														redirectLink={`child-safety-standards/${item.id}`}
													/>
												);
											})
										) : (
											<NoInfoMessage name="Child Safe Standards" />
										)}
									</ul>
								</div>

								<ScrollToTopButton extraClass="mtop-16" />
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default ChildSafeStandards;
